/**
 * Why a program meets as often as it does.
 *
 * Three separate rules decide whether a class happens on a given day — its cron weekdays,
 * the school-wide calendar in `program_closures`, and its own `programs.excluded_dates` —
 * and until now nobody could see the result. An admin looking at Friday Basketball could
 * read "11 sessions" in the description and had no way to check it, and a parent had only
 * whatever the vendor happened to type.
 *
 * This derives the answer once, with the reason attached to each cancelled day, so the
 * admin panel, the parent programme page and any audit all quote the same number.
 */

import { getSessionWeekdays, shiftDateString, weekdayOfDateString } from './schedule';
import { DEFAULT_BRAND_KEY, type BrandKey } from '../config/brands';
import {
  closureLabelForProgramType,
  isClosedForProgramType,
} from './programCalendar';

/** The slice of a program this needs. Matches `SessionProgram` in staff-app. */
export interface BreakdownProgram {
  type: string;
  schedule_cron: string;
  start_date: string;
  end_date: string;
  excluded_dates?: string[] | null;
  /**
   * Whose closure calendar this program keeps. Optional — absent reads as
   * `DEFAULT_BRAND_KEY`, which is what every row selected without the column means.
   *
   * This is billing math: `sessions = cron weekdays − closures − excluded_dates`. Reading
   * the wrong brand's calendar here either invents a session the school is shut for or
   * deletes one it is open for, so the brand has to travel with the program.
   */
  brand_key?: BrandKey;
}

/** A day the cron claims but the program does not actually meet. */
export interface SkippedDay {
  /** YYYY-MM-DD, business TZ. */
  date: string;
  /**
   * `school` — a `program_closures` day, shared with every other program of its audience.
   * `program` — this program's own `excluded_dates`, a vendor diverging from the school.
   */
  source: 'school' | 'program';
  /** The closure's label; undefined for a program-specific date, which carries no reason. */
  reason?: string;
}

export interface SessionBreakdown {
  /** Days the program genuinely meets, oldest first. */
  sessions: string[];
  /** Days its cron claims but that are cancelled, oldest first. */
  skipped: SkippedDay[];
  /** `sessions.length` — the number a description should agree with. */
  sessionCount: number;
  /** Every day the cron names in range, before any cancellation. */
  cronDayCount: number;
}

/**
 * Walk the program's term and split its cron days into met and skipped.
 *
 * Camps are exempt from both calendars — they run on the very days that close after-school
 * — so their breakdown is simply every cron day in range.
 */
export function sessionBreakdown(program: BreakdownProgram): SessionBreakdown {
  const weekdays = getSessionWeekdays(program.schedule_cron);
  const excluded = new Set(program.excluded_dates ?? []);
  const isCamp = program.type?.toLowerCase() === 'camp';
  const brand = program.brand_key ?? DEFAULT_BRAND_KEY;

  const sessions: string[] = [];
  const skipped: SkippedDay[] = [];
  let cronDayCount = 0;

  for (let d = program.start_date; d <= program.end_date; d = shiftDateString(d, 1)) {
    if (!weekdays.has(weekdayOfDateString(d))) continue;
    cronDayCount += 1;

    if (isCamp) {
      sessions.push(d);
      continue;
    }
    // Program-specific first, so a vendor day off is reported as the vendor's own choice
    // even on a date the school also happens to close.
    if (excluded.has(d)) {
      skipped.push({ date: d, source: 'program' });
    } else if (isClosedForProgramType(program.type, d, brand)) {
      skipped.push({
        date: d,
        source: 'school',
        reason: closureLabelForProgramType(program.type, d, brand),
      });
    } else {
      sessions.push(d);
    }
  }

  return { sessions, skipped, sessionCount: sessions.length, cronDayCount };
}

/**
 * "15 Fridays − 3 school closures − 1 program date = 11 sessions".
 *
 * Written out rather than reduced to a total because the total is the thing people doubt;
 * seeing the subtraction is what makes it checkable.
 */
export function describeBreakdown(b: SessionBreakdown): string {
  const school = b.skipped.filter(s => s.source === 'school').length;
  const own = b.skipped.filter(s => s.source === 'program').length;
  const parts = [`${b.cronDayCount} scheduled day${b.cronDayCount === 1 ? '' : 's'}`];
  if (school > 0) parts.push(`− ${school} school closure${school === 1 ? '' : 's'}`);
  if (own > 0) parts.push(`− ${own} program date${own === 1 ? '' : 's'}`);
  return `${parts.join(' ')} = ${b.sessionCount} session${b.sessionCount === 1 ? '' : 's'}`;
}

/**
 * A closure label as a family should read it.
 *
 * Stored labels carry the audience clause an admin needs when authoring the calendar —
 * "Fall Camp — no enrichment", "Winter Camp (no after-school)". On a parent's programme page
 * that clause is noise at best: they are looking at one class, so which audience is affected
 * is already settled, and "no enrichment" is internal vocabulary that means nothing to them.
 *
 * The reason itself is kept, because it is the useful half — "Fall Camp" tells a parent why.
 */
export function parentClosureLabel(label: string | undefined): string | undefined {
  if (!label) return undefined;
  const trimmed = label
    .replace(/\s*[—–-]\s*no[- ]enrichment\s*$/i, '')
    .replace(/\s*\(\s*no after-school\s*\)\s*$/i, '')
    .trim();
  return trimmed.length > 0 ? trimmed : undefined;
}
